import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * PdfPreview — renders EVERY page of a pdfmake docDef as stacked PNG images.
 *
 * Why not an <iframe src={blobUrl}>? iOS Safari renders only page 1 of a PDF
 * inside an iframe (its built-in viewer shows a single-page Quick Look), so a
 * multi-page rider preview was stuck on page 1 on iPad/iPhone. We instead send
 * the docDef to the server, which rasterizes it with pdftoppm into per-page
 * PNG data URLs and returns them here — displayed stacked in a scrollable box,
 * identical on every device (including the native PDF viewer for download).
 */
interface PdfPreviewProps {
  /** The pdfmake document definition. Changing identity triggers a re-render. */
  docDef: unknown;
  /** Scrollable preview box height (px). Defaults to 600. */
  height?: number;
}

export default function PdfPreview({ docDef, height = 600 }: PdfPreviewProps) {
  const { t } = useTranslation();
  const [pages, setPages] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const genRef = useRef(0);

  useEffect(() => {
    if (!docDef) { setPages(null); setError(null); return; }
    const gen = ++genRef.current;
    setError(null);
    // Stale-while-revalidate: keep the previous pages visible while the new
    // doc renders, so the preview box keeps its height and the page does NOT
    // jump back to the top on every keystroke of a live editor. Only the very
    // first load shows the "loading" placeholder.
    fetch('/api/pdf/pages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ docDef }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j?.error || j?.detail || `Preview failed (${r.status})`);
        }
        return r.json();
      })
      .then((d) => { if (gen === genRef.current) setPages(d?.pages || []); })
      .catch((e) => { if (gen === genRef.current) setError(e.message || 'Preview failed'); });
    return () => { /* cancel via gen token */ };
  }, [docDef]);

  if (error) {
    return (
      <div className="rider-preview-placeholder rider-preview-error">PDF preview error: {error}</div>
    );
  }
  if (!pages) {
    return <div className="rider-preview-placeholder">{t('pdfPreview.loading')}</div>;
  }
  return (
    <div
      className="rider-preview-scroll"
      style={{
        height,
        overflowY: 'auto',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        background: '#4a4a52',
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {pages.map((src, i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            boxShadow: '0 4px 12px rgba(0,0,0,.4)',
            borderRadius: 4,
            padding: 4,
            flexShrink: 0,
          }}
        >
          <img
            src={src}
            alt={`PDF page ${i + 1}`}
            style={{ width: '100%', display: 'block' }}
          />
        </div>
      ))}
    </div>
  );
}
